/**
 * SOP Toolkit - Sales Order Processing
 *
 * Creates a new job in PrintLogic via secure backend route,
 * then allows artwork uploads + Master Folder download.
 * Access: PIN protected
 *
 * Usage: /sop-toolkit
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  ClipboardList, User, FileText, Briefcase, Calendar, Hash, Euro,
  AlertTriangle, Lock, ArrowLeft, X, Loader2, ScanLine, Sparkles
} from "lucide-react";
import SOPJobWorkspace from "./SOPJobWorkspace";
import TerminologyPanel from "./TerminologyPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOPToolkit = () => {
  const [pin, setPin] = useState('');
  const [kioskKey, setKioskKey] = useState('');
  const [verified, setVerified] = useState(false);
  const [checkingKey, setCheckingKey] = useState(true);
  const [pinError, setPinError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [customer, setCustomer] = useState('');
  const [details, setDetails] = useState('');
  const [repName, setRepName] = useState('');
  const [dateRequired, setDateRequired] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [poFileToken, setPoFileToken] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractNotice, setExtractNotice] = useState('');
  const poInputRef = useRef(null);

  const [customerStatus, setCustomerStatus] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const lookupTimer = useRef(null);
  const detailsRef = useRef(null);

  const EMPTY_DOCKET = { time_required: '', origination: '', materials: '', printing: '', finishing: '', fulfilment: '', docket_notes: '' };
  const [docket, setDocket] = useState(EMPTY_DOCKET);
  const [docketOpen, setDocketOpen] = useState(false);
  const setDocketField = (k) => (e) => setDocket((d) => ({ ...d, [k]: e.target.value }));

  const PLACEHOLDER_RE = /\[[^\][]*\]/g;
  const placeholderCount = (details.match(PLACEHOLDER_RE) || []).length;

  const selectPlaceholderAt = (value, fromIndex) => {
    const el = detailsRef.current;
    if (!el) return false;
    PLACEHOLDER_RE.lastIndex = 0;
    let first = null, target = null, m;
    while ((m = PLACEHOLDER_RE.exec(value)) !== null) {
      if (first === null) first = m;
      if (m.index >= fromIndex && target === null) target = m;
    }
    const pick = target || first;
    if (!pick) return false;
    el.focus();
    el.setSelectionRange(pick.index, pick.index + pick[0].length);
    return true;
  };

  const insertPhrase = (phrase) => {
    const base = details.trim() ? details.replace(/\s+$/, '') + '\n' : '';
    const newVal = base + phrase;
    setDetails(newVal);
    setTimeout(() => selectPlaceholderAt(newVal, base.length), 30);
  };

  const handleDetailsKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey && placeholderCount > 0) {
      const el = detailsRef.current;
      const from = el ? el.selectionEnd : 0;
      e.preventDefault();
      selectPlaceholderAt(details, from);
    }
  };

  // Load full customer directory for type-ahead once unlocked
  useEffect(() => {
    if (!verified) return;
    const auth = kioskKey ? `key=${encodeURIComponent(kioskKey)}` : `pin=${encodeURIComponent(pin)}`;
    axios.get(`${API}/sop/customers?${auth}`)
      .then(res => setCustomerList(res.data.customers || []))
      .catch(() => {});
  }, [verified, pin, kioskKey]);

  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdJob, setCreatedJob] = useState(null);

  // Kiosk-key auto-unlock removed for security — PIN is the only way in.
  useEffect(() => {
    setCheckingKey(false);
  }, []);

  const verifyPin = async () => {
    setLoading(true);
    setPinError(false);
    try {
      const res = await axios.get(`${API}/sop/verify-access?pin=${encodeURIComponent(pin)}`);
      if (res.data.valid) {
        setVerified(true);
      } else {
        setPinError(true);
      }
    } catch (err) {
      setPinError(true);
    } finally {
      setLoading(false);
    }
  };

  const runCustomerLookup = async (name, currentPin, currentKey) => {
    if (!name.trim()) {
      setCustomerStatus(null);
      return;
    }
    try {
      const auth = currentKey ? `key=${encodeURIComponent(currentKey)}` : `pin=${encodeURIComponent(currentPin)}`;
      const res = await axios.get(`${API}/sop/customer-lookup?name=${encodeURIComponent(name.trim())}&${auth}`);
      setCustomerStatus({ matched: res.data.matched, customer: res.data.customer });
    } catch (err) {
      setCustomerStatus(null);
    }
  };

  const filterCustomers = (val) => {
    const q = val.trim().toLowerCase();
    if (!q) return [];
    return customerList.filter(n => n.toLowerCase().includes(q) && n.toLowerCase() !== q).slice(0, 8);
  };

  const clearCustomerFieldError = () => {
    setFieldErrors(prev => {
      if (!prev.customer) return prev;
      const { customer: _omit, ...rest } = prev;
      return rest;
    });
  };

  const onCustomerChange = (val) => {
    setCustomer(val);
    setCustomerStatus(null);
    clearCustomerFieldError();
    setSuggestions(filterCustomers(val));
    setDropdownOpen(true);
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => runCustomerLookup(val, pin, kioskKey), 400);
  };

  const pickSuggestion = (name) => {
    setCustomer(name);
    clearCustomerFieldError();
    setSuggestions([]);
    setDropdownOpen(false);
    clearTimeout(lookupTimer.current);
    runCustomerLookup(name, pin, kioskKey);
  };

  const extractPO = async (file) => {
    setExtracting(true);
    setExtractNotice('');
    setError('');
    try {
      const fd = new FormData();
      fd.append('pin', pin);
      fd.append('kiosk_key', kioskKey);
      fd.append('file', file);
      const res = await axios.post(`${API}/sop/extract-po`, fd);
      const ex = res.data.extracted;
      if (ex.customer) {
        setCustomer(ex.customer);
        setSuggestions(filterCustomers(ex.customer));
        runCustomerLookup(ex.customer, pin, kioskKey);
      }
      if (ex.details) setDetails(ex.details);
      if (ex.quantity) setQuantity(ex.quantity);
      if (ex.price) setPrice(ex.price);
      if (ex.po_number) setPoNumber(ex.po_number);
      if (ex.date_required) setDateRequired(ex.date_required);
      const docketFill = {};
      if (ex.materials) docketFill.materials = ex.materials;
      if (ex.printing) docketFill.printing = ex.printing;
      if (ex.finishing) docketFill.finishing = ex.finishing;
      if (ex.time_required) docketFill.time_required = ex.time_required;
      if (Object.keys(docketFill).length) {
        setDocket((d) => ({ ...d, ...docketFill }));
        setDocketOpen(true);
      }
      setPoFileToken(res.data.po_file_token);
      setExtractNotice(`Auto-filled from "${res.data.filename}" — please review before creating. The PO will be saved to the job's PO & Other folder.`);
      setFieldErrors({});
    } catch (err) {
      const data = err.response?.data;
      if (data?.po_file_token) setPoFileToken(data.po_file_token);
      setError(data?.message || 'Could not read the PO. Please fill in the details manually.');
    } finally {
      setExtracting(false);
    }
  };

  const isKnownCustomer = (val) => {
    const q = (val || '').trim().toLowerCase();
    return !!q && customerList.some(n => n.toLowerCase() === q);
  };

  const customerOk = () => customerStatus?.matched || isKnownCustomer(customer);

  const validate = () => {
    const errors = {};
    if (!customer.trim()) errors.customer = 'Customer is required';
    else if (!customerOk()) errors.customer = 'Select an existing PrintLogic customer from the list';
    if (!details.trim()) errors.details = 'Details are required';
    else if (placeholderCount > 0) errors.details = `Fill in the ${placeholderCount} [BRACKETED] placeholder${placeholderCount > 1 ? 's' : ''} before creating the job`;
    if (!repName.trim()) errors.rep_name = 'Rep name is required';
    if (!dateRequired) errors.date_required = 'Date required is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (validate()) setShowConfirm(true);
  };

  const confirmCreate = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/print-logic/jobs`, {
        pin,
        kiosk_key: kioskKey,
        customer: customer.trim(),
        details: details.trim(),
        rep_name: repName.trim(),
        date_required: dateRequired,
        po_number: poNumber.trim(),
        quantity: quantity.trim(),
        price: price.trim(),
        po_file_token: poFileToken,
        time_required: docket.time_required.trim(),
        origination: docket.origination.trim(),
        materials: docket.materials.trim(),
        printing: docket.printing.trim(),
        finishing: docket.finishing.trim(),
        fulfilment: docket.fulfilment.trim(),
        docket_notes: docket.docket_notes.trim()
      });
      setCreatedJob({ job: res.data.job, masterFolderName: res.data.masterFolderName, customerMatched: res.data.customerMatched });
      setShowConfirm(false);
      setCustomer('');
      setCustomerStatus(null);
      setSuggestions([]);
      setDetails('');
      setRepName('');
      setDateRequired('');
      setPoNumber('');
      setQuantity('');
      setPrice('');
      setPoFileToken('');
      setExtractNotice('');
      setDocket(EMPTY_DOCKET);
      setDocketOpen(false);
      setFieldErrors({});
    } catch (err) {
      setShowConfirm(false);
      const data = err.response?.data;
      if (data?.fieldErrors) setFieldErrors(data.fieldErrors);
      setError(data?.message || 'Unable to create the Print Logic job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full pl-10 pr-4 py-3 border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition-colors ${
      fieldErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-emerald-500'
    }`;

  if (checkingKey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-700 to-teal-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
    );
  }

  // PIN Entry Screen
  if (!verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-700 to-teal-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" data-testid="sop-pin-screen">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">SOP Toolkit</h1>
            <p className="text-gray-500 mt-2">Enter PIN to continue</p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && verifyPin()}
                placeholder="Enter PIN"
                aria-label="PIN"
                data-testid="sop-pin-input"
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-lg text-gray-900 placeholder-gray-400 focus:outline-none transition-colors ${
                  pinError ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-emerald-500'
                }`}
                autoFocus
              />
            </div>

            {pinError && (
              <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="sop-pin-error">
                <AlertTriangle className="w-4 h-4" />
                <span>Invalid PIN. Please try again.</span>
              </div>
            )}

            <button
              onClick={verifyPin}
              disabled={loading || !pin}
              data-testid="sop-pin-submit-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Job Workspace (uploads + download) after successful creation
  if (createdJob) {
    return (
      <SOPJobWorkspace
        job={createdJob.job}
        masterFolderName={createdJob.masterFolderName}
        customerMatched={createdJob.customerMatched}
        pin={pin}
        kioskKey={kioskKey}
        onCreateAnother={() => setCreatedJob(null)}
      />
    );
  }

  // Form
  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <ClipboardList className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">SOP Toolkit</h1>
          <p className="text-gray-500">Create a new job in Print Logic</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 space-y-5" noValidate>
          {/* Auto-fill from PO */}
          <div className="border-2 border-dashed border-emerald-300 bg-emerald-50/50 rounded-xl p-4">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="flex items-center gap-3 flex-1">
                <ScanLine className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-gray-800 text-sm">Have a customer PO?</p>
                  <p className="text-xs text-gray-500">Upload it and we'll auto-fill the form for you</p>
                </div>
              </div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                ref={poInputRef}
                onChange={(e) => {
                  if (e.target.files?.[0]) extractPO(e.target.files[0]);
                  e.target.value = '';
                }}
                data-testid="sop-po-file-input"
              />
              <button
                type="button"
                onClick={() => poInputRef.current?.click()}
                disabled={extracting}
                data-testid="sop-extract-po-btn"
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white transition-colors flex items-center justify-center gap-2"
              >
                {extracting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Reading PO...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Upload PO to Auto-fill
                  </>
                )}
              </button>
            </div>
          </div>

          {extractNotice && (
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-4 py-3 rounded-xl text-sm" data-testid="sop-extract-notice">
              <Sparkles className="w-5 h-5 flex-shrink-0" />
              <span>{extractNotice}</span>
            </div>
          )}

          {/* Customer */}
          <div>
            <label htmlFor="sop-customer" className="block text-sm font-medium text-gray-700 mb-2">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                id="sop-customer"
                type="text"
                value={customer}
                onChange={(e) => onCustomerChange(e.target.value)}
                onFocus={() => {
                  setSuggestions(filterCustomers(customer));
                  setDropdownOpen(true);
                }}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                placeholder="Start typing to search customers..."
                autoComplete="off"
                data-testid="sop-customer-input"
                className={inputClass('customer')}
              />
              {dropdownOpen && suggestions.length > 0 && (
                <ul
                  className="absolute z-20 left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-56 overflow-y-auto"
                  data-testid="sop-customer-suggestions"
                >
                  {suggestions.map((s, i) => (
                    <li key={s}>
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                        data-testid={`sop-customer-suggestion-${i}`}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      >
                        {s}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {fieldErrors.customer ? (
              <p className="text-red-600 text-sm mt-1 flex items-center gap-1" data-testid="sop-customer-error">
                <AlertTriangle className="w-4 h-4" /> {fieldErrors.customer}
              </p>
            ) : customer.trim() ? (
              (customerStatus?.matched || isKnownCustomer(customer)) ? (
                <p className="text-emerald-700 text-sm mt-1 flex items-center gap-1" data-testid="sop-customer-status-matched">
                  <Sparkles className="w-4 h-4" /> Existing PrintLogic customer — job will be linked
                  {customerStatus?.customer?.customer_name ? ` as "${customerStatus.customer.customer_name}"` : ''}
                </p>
              ) : (customerStatus && !customerStatus.matched) ? (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1" data-testid="sop-customer-status-new">
                  <AlertTriangle className="w-4 h-4" /> Not found in PrintLogic — select an existing customer from the list
                </p>
              ) : null
            ) : null}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="sop-details" className="block text-sm font-medium text-gray-700">
                Details <span className="text-red-500">*</span>
              </label>
              <a
                href={`${API}/sop/reference-doc?${kioskKey ? `key=${encodeURIComponent(kioskKey)}` : `pin=${encodeURIComponent(pin)}`}`}
                data-testid="sop-terminology-doc-link"
                className="text-xs font-bold text-emerald-600 hover:text-emerald-800 underline underline-offset-2"
              >
                Docket Terminology Guide (.docx)
              </a>
            </div>
            <div className="relative">
              <FileText className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <textarea
                id="sop-details"
                ref={detailsRef}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                onKeyDown={handleDetailsKeyDown}
                placeholder="Enter job details"
                rows={4}
                data-testid="sop-details-input"
                className={`${inputClass('details')} resize-none`}
              />
            </div>
            {placeholderCount > 0 && (
              <p className="text-amber-600 text-xs mt-1 flex items-center gap-1 font-bold" data-testid="sop-placeholder-hint">
                <AlertTriangle className="w-3.5 h-3.5" />
                {placeholderCount} placeholder{placeholderCount > 1 ? 's' : ''} to fill — press Tab to jump to the next one
              </p>
            )}
            {fieldErrors.details && (
              <p className="text-red-600 text-sm mt-1 flex items-center gap-1" data-testid="sop-details-error">
                <AlertTriangle className="w-4 h-4" /> {fieldErrors.details}
              </p>
            )}
            <TerminologyPanel
              pin={pin}
              kioskKey={kioskKey}
              onInsert={insertPhrase}
            />
          </div>

          {/* Quantity + Price */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sop-quantity" className="block text-sm font-medium text-gray-700 mb-2">
                Quantity <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="sop-quantity"
                  type="text"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g. 16"
                  data-testid="sop-quantity-input"
                  className={inputClass('quantity')}
                />
              </div>
            </div>
            <div>
              <label htmlFor="sop-price" className="block text-sm font-medium text-gray-700 mb-2">
                Price <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="sop-price"
                  type="text"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 80.00"
                  data-testid="sop-price-input"
                  className={inputClass('price')}
                />
              </div>
              {fieldErrors.price && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1" data-testid="sop-price-error">
                  <AlertTriangle className="w-4 h-4" /> {fieldErrors.price}
                </p>
              )}
            </div>
          </div>

          {/* Date Required + PO Number */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sop-date" className="block text-sm font-medium text-gray-700 mb-2">
                Date Required <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="sop-date"
                  type="date"
                  value={dateRequired}
                  onChange={(e) => setDateRequired(e.target.value)}
                  data-testid="sop-date-input"
                  className={inputClass('date_required')}
                />
              </div>
              {fieldErrors.date_required && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1" data-testid="sop-date-error">
                  <AlertTriangle className="w-4 h-4" /> {fieldErrors.date_required}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="sop-po" className="block text-sm font-medium text-gray-700 mb-2">
                PO Number <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="sop-po"
                  type="text"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="e.g. PO-12345"
                  data-testid="sop-po-input"
                  className={inputClass('po_number')}
                />
              </div>
            </div>
          </div>

          {/* Rep Name */}
          <div>
            <label htmlFor="sop-rep" className="block text-sm font-medium text-gray-700 mb-2">
              Rep Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="sop-rep"
                type="text"
                value={repName}
                onChange={(e) => setRepName(e.target.value)}
                placeholder="Enter representative name"
                data-testid="sop-rep-input"
                className={inputClass('rep_name')}
              />
            </div>
            {fieldErrors.rep_name && (
              <p className="text-red-600 text-sm mt-1 flex items-center gap-1" data-testid="sop-rep-error">
                <AlertTriangle className="w-4 h-4" /> {fieldErrors.rep_name}
              </p>
            )}
          </div>

          {/* Docket Details (optional, printed on the job docket) */}
          <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setDocketOpen((o) => !o)}
              data-testid="sop-docket-toggle"
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="text-sm font-bold text-gray-700">
                Docket Details <span className="font-normal text-gray-400">(optional — printed on the job docket)</span>
              </span>
              <span className="flex items-center gap-2">
                {Object.values(docket).filter((v) => v.trim()).length > 0 && (
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    {Object.values(docket).filter((v) => v.trim()).length} filled
                  </span>
                )}
                <span className="text-gray-400 text-lg leading-none">{docketOpen ? '−' : '+'}</span>
              </span>
            </button>
            {docketOpen && (
              <div className="p-4 space-y-3 bg-white" data-testid="sop-docket-section">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ['materials', 'Material', 'e.g. EB Flute'],
                    ['printing', 'Printing', 'e.g. Printed both sides'],
                    ['finishing', 'Finishing', 'e.g. Trim to size'],
                    ['origination', 'Origination', 'e.g. Artwork supplied'],
                    ['fulfilment', 'Fulfilment / Colour Digital', 'e.g. Pack in 4s'],
                    ['time_required', 'Time Required', 'e.g. 10am'],
                  ].map(([key, label, ph]) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                      <input
                        type="text"
                        value={docket[key]}
                        onChange={setDocketField(key)}
                        placeholder={ph}
                        data-testid={`sop-docket-${key}`}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-gray-900 placeholder-gray-400"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Docket Notes</label>
                  <textarea
                    value={docket.docket_notes}
                    onChange={setDocketField('docket_notes')}
                    rows={2}
                    placeholder="Any other production notes for the docket…"
                    data-testid="sop-docket-notes"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none resize-none text-gray-900 placeholder-gray-400"
                  />
                </div>
                <p className="text-xs text-gray-400">These are added to the job description so they print on the docket. Uploading a PO pre-fills what it can.</p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl" data-testid="sop-error-message">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="sop-create-job-btn"
            className="w-full py-4 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 transition-all flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Job...
              </>
            ) : (
              <>
                <Briefcase className="w-5 h-5" />
                Create Print Logic Job
              </>
            )}
          </button>
        </form>

        <p className="text-center text-gray-400 text-sm mt-6">
          Jobs are created directly in Print Logic
        </p>
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="sop-confirm-dialog">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Confirm New Job</h2>
              <button
                onClick={() => setShowConfirm(false)}
                aria-label="Close"
                data-testid="sop-confirm-close-btn"
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3 text-sm">
              <div>
                <p className="text-gray-500">Customer</p>
                <p className="font-bold text-gray-800" data-testid="sop-confirm-customer">{customer.trim()}</p>
                {(customerStatus?.matched || isKnownCustomer(customer)) && (
                  <p className="text-xs mt-0.5 text-emerald-600">Existing customer — will be linked</p>
                )}
              </div>
              <div>
                <p className="text-gray-500">Details</p>
                <p className="font-bold text-gray-800 whitespace-pre-wrap" data-testid="sop-confirm-details">{details.trim()}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500">Quantity</p>
                  <p className="font-bold text-gray-800" data-testid="sop-confirm-quantity">{quantity.trim() || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Price</p>
                  <p className="font-bold text-gray-800" data-testid="sop-confirm-price">{price.trim() ? `€${price.trim()}` : '—'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500">Date Required</p>
                  <p className="font-bold text-gray-800" data-testid="sop-confirm-date">{dateRequired}</p>
                </div>
                <div>
                  <p className="text-gray-500">PO Number</p>
                  <p className="font-bold text-gray-800" data-testid="sop-confirm-po">{poNumber.trim() || '—'}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-500">Rep Name</p>
                <p className="font-bold text-gray-800" data-testid="sop-confirm-rep">{repName.trim()}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                data-testid="sop-confirm-cancel-btn"
                className="py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmCreate}
                disabled={submitting}
                data-testid="sop-confirm-create-btn"
                className="py-3 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPToolkit;
